import { promiseState } from '@passionware/platform-react';
import type { Resource } from '__types__';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { clearUpdatePayload } from 'store/v1/contacts/contacts.actions';
import { Button } from 'v5/design-system/Button';
import { Input2 } from 'v5/design-system/Input2';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from 'v5/design-system/Popover';
import { AsyncButton } from 'v5/patterns/forms/fields/AsyncButton';
import { WithServices } from 'v5/platform/ts/services';
import { WithResourceMutationService } from 'v5/services/io/ResourceMutationService/ResourceMutationService';

export type RequestAvailabilityAddEmailProps = WithServices<
  [WithResourceMutationService]
> & {
  resource: Resource;
  onSaved: (email: string) => void;
};

export function RequestAvailabilityAddEmail({
  resource,
  onSaved,
  services
}: RequestAvailabilityAddEmailProps) {
  const { t } = useTranslation('availability-request', {
    keyPrefix: 'RequestAvailabilityDialog'
  });
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');

  const saveMutation = promiseState.useMutation(async (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      throw new Error(t('emailRequired'));
    }
    // Only patch emails — spreading the React Query resource can include graph
    // fields that make PUT /contacts 500.
    const data = clearUpdatePayload({
      id: resource.id,
      emails: [{ value_1: trimmed }]
    });
    await services.resourceMutationService.updateResource({
      id: resource.id,
      data
    });
    onSaved(trimmed);
    setEmail('');
    setOpen(false);
  });

  return (
    <Popover
      open={open}
      onOpenChange={next => {
        setOpen(next);
        if (!next) {
          setEmail('');
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button type="button" variant="secondary" size="sm">
          {t('addEmail')}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 space-y-3 p-3">
        <div className="space-y-1.5">
          <p className="text-base font-medium text-fg-default">
            {t('resourceEmail')}
          </p>
          <Input2
            size="md"
            variant="default"
            type="email"
            autoComplete="off"
            placeholder={t('emailPlaceholder')}
            value={email}
            onChange={event => setEmail(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void saveMutation.track(email);
              }
            }}
          />
        </div>
        <AsyncButton
          type="button"
          variant="primary"
          size="sm"
          className="w-full"
          mutation={saveMutation.state}
          disabled={!email.trim()}
          onClick={() => {
            void saveMutation.track(email);
          }}
        >
          {t('saveEmail')}
        </AsyncButton>
      </PopoverContent>
    </Popover>
  );
}
